import type { FC } from 'react';
import React, { useCallback, useMemo } from 'react';
import { ConnectAccountContent } from '../components/connect-account/ConnectAccountContent';
import {
  useAcceptInvitationPhoenix,
  useGetAllCountriesPhoenix,
  useGetNursingHomeList,
  useServiceProvider,
  useUpdateServiceProvider,
  useUpdateUserProfile,
  useUserProfile,
} from '@pflegenavi/service-provider-frontend/api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { spAppMainPages } from '@pflegenavi/frontend/routing';
import type { IConnectAccountFormValues } from '../components/connect-account/ConnectForm';
import { useAuthentication } from '@pflegenavi/frontend/authentication';
import { sanitizeString } from '@pflegenavi/shared/utils';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { CircularProgress, Stack } from '@mui/material';
import { keepPreviousData } from '@tanstack/react-query';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface NewInvitationPageProps {}

export const ConnectAccountPage: FC<NewInvitationPageProps> = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const tenant = searchParams.get('tenant');
  const nursingHomeId = searchParams.get('nursingHomeId');
  const globalServiceProviderId = searchParams.get('globalServiceProviderId');
  const { user } = useAuthentication();
  const {
    mutateAsync: acceptInvitationPhoenix,
    isPending: acceptInvitationPhoenixLoading,
  } = useAcceptInvitationPhoenix();

  const {
    mutateAsync: updateUserProfile,
    isPending: updateUserProfileLoading,
  } = useUpdateUserProfile();
  const {
    mutateAsync: updateServiceProvider,
    isPending: updateServiceProviderLoading,
  } = useUpdateServiceProvider();
  const { data: nursingHomeList } = useGetNursingHomeList();
  const { data: userProfile, isPending: isUserProfileLoading } = useUserProfile(
    {}
  );

  const userId = user?.userId;

  if (!tenant || !globalServiceProviderId) {
    throw new Error('Missing required parameters for invitation');
  }

  const { data: serviceProvider } = useServiceProvider(
    {
      globalServiceProviderId: globalServiceProviderId,
    },
    {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }
  );

  const { data: countries, isPending: areCountriesLoading } =
    useGetAllCountriesPhoenix({
      refetchOnWindowFocus: false,
      placeholderData: keepPreviousData,
    });

  // The nursing home is not there if a) the nursing home does not exist or b) the invitation is not valid anymore
  const nursingHome = useMemo(() => {
    return nursingHomeList?.find(
      (nursingHome) => nursingHome.nursingHomeId === nursingHomeId
    );
  }, [nursingHomeList, nursingHomeId]);

  const userDetails = useMemo(() => {
    return {
      firstName:
        userProfile?.user.firstName &&
        userProfile.user.firstName !== userProfile.user.eMail // The endpoint returns the email address also in the firstName field if no firstName is set
          ? userProfile.user.firstName
          : '',
      lastName: userProfile?.user.lastName ?? '',
    };
  }, [userProfile]);

  const companyDetails = useMemo(() => {
    return {
      name: serviceProvider?.name ?? '',
      address: serviceProvider?.address ?? '',
      city: serviceProvider?.city ?? '',
      zipCode: serviceProvider?.zipCode ?? '',
      phone: serviceProvider?.phone ?? '',
      countryId: serviceProvider?.countryId ?? '',
    };
  }, [serviceProvider]);

  const onInvitationResponse = useCallback(
    async (
      values: IConnectAccountFormValues,
      responseToInvitation?: boolean
    ) => {
      if (!userId) {
        throw new Error('Id of logged in user could not be obtained');
      }

      await updateUserProfile({
        id: userId,
        firstName: sanitizeString(values.firstName),
        lastName: sanitizeString(values.lastName),
      });

      await updateServiceProvider({
        globalServiceProviderId: globalServiceProviderId,
        data: {
          name: sanitizeString(values.companyName),
          address: sanitizeString(values.address),
          city: sanitizeString(values.city),
          zipCode: sanitizeString(values.zipCode),
          phone: sanitizeString(values.phone),
          countryId: values.countryId,
        },
      });

      if (nursingHome && responseToInvitation) {
        await acceptInvitationPhoenix({
          nursing_home_id: nursingHomeId as string,
        });
        enqueueSnackbar(
          t('service-provider-app.connect-account.invitation-accepted'),
          { variant: 'success' }
        );
      } else {
        // If the nursing home is not there, it either does not exist (unlikely) or the invitation is not valid anymore.
        // In that case, the user can still set up their account details, but is not affiliated with a nursing home (but
        // may be invited by one later on).
        enqueueSnackbar(t('service-provider-app.connect-account.data-saved'), {
          variant: 'success',
        });
      }

      navigate(spAppMainPages.HOME);
    },
    [
      userId,
      updateUserProfile,
      updateServiceProvider,
      globalServiceProviderId,
      nursingHome,
      navigate,
      enqueueSnackbar,
      t,
      acceptInvitationPhoenix,
      nursingHomeId,
    ]
  );

  if (isUserProfileLoading || areCountriesLoading) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{
          height: '100%',
          overflow: 'hidden',
          width: '100%',
          display: 'flex',
        }}
      >
        <CircularProgress color="primary" variant="indeterminate" />
      </Stack>
    );
  }

  return (
    <ConnectAccountContent
      userDetails={userDetails}
      companyDetails={companyDetails}
      countryOptions={countries ?? []}
      loading={
        isUserProfileLoading ||
        areCountriesLoading ||
        updateUserProfileLoading ||
        updateServiceProviderLoading ||
        acceptInvitationPhoenixLoading
      }
      accountAffiliationName={nursingHome?.nursingHomeName}
      connectionStatus={nursingHome?.connectionStatus}
      handleConnectAccount={onInvitationResponse}
    />
  );
};
